import { useId } from "react";

import { AdjustFontSizeToFitContent } from "@newsela/angelou";
import ExportablesTemplateHeaderSection from "@/components/AIComponents/ExportablesModal/templates/components/ExportablesTemplateHeaderSection";
import { TemplateTextArea } from "@/components/AIComponents/ExportablesModal/templates/components/TemplateTextArea";
import {
  containerStylesCss,
  exampleTextAreaContainerCss,
  exampleLabelCss,
  exampleTextareaCss,
  leftArmContainerCss,
  rightArmContainerCss,
  targetWordAndArmsCss,
  targetWordContainerCss,
  wordAndArrowContainerCss,
  wordComponentInputCss,
  wordComponentLabelCss,
  wordComponentColumnCss,
  wordComponentsGridCss,
  wordContainerCss,
  wordHeaderStylesCss,
  wordInputStylesCss,
  wordSectionCss,
  arrowLineRightCss,
  wordComponentRowCss,
  targetWordAndArrowContainerCss,
} from "./WordStudyOrganizer.styles";
import {
  WORD_MAX_LENGTH,
  WORD_COMPONENT_FIELDS,
  WORD_DEFINITION_AND_EXAMPLES_FIELDS,
} from "./wordStudyFieldConfigs";
import ArrowDown from "./ArrowDown";
import ArrowLineLeft from "./ArrowLineLeft";
import type { TemplateProps } from "@/components/AIComponents/ExportablesModal/templates/registry";
import type { WordStudyFormData } from "@/types/models/Exportables.interface";

/**
 * Word Study Organizer - Helps students analyze word structure by breaking down
 * words into their components (prefix, root word, suffix) with meanings and usage examples.
 *
 * @see https://newsela.atlassian.net/browse/LESS-4590 - LESS-4590
 * @see https://www.figma.com/design/nI09R8SWrbPnVl32GfWnvE/Graphic-Organizers?node-id=143-13497
 *
 * @param formData - The form data containing word structure fields (flat structure)
 * @param onChange - Callback when data changes
 * @param title - Display title for the organizer
 * @param isStudent - Whether rendering for student view (read-only)
 */

const WordStudyOrganizer = ({
  formData,
  onChange,
  title = "Word investigator",
  isStudent = false,
}: TemplateProps<WordStudyFormData>) => {
  const uniqueId = useId();

  const organizerData = formData ?? ({} as WordStudyFormData);

  // Normalize data by converting nulls/undefined to empty strings
  const wordData = Object.fromEntries(
    Object.entries(organizerData).map(([key, value]) => [key, value ?? ""]),
  ) as WordStudyFormData & Record<string, string>;

  const articleTitle =
    wordData?.article_title || wordData?.articleShortTitle || "";

  return (
    <div css={containerStylesCss}>
      <ExportablesTemplateHeaderSection
        templateTitle={title}
        articleTitle={articleTitle}
      />

      <div css={wordSectionCss}>
        <div css={targetWordAndArmsCss}>
          <div css={leftArmContainerCss}>
            <ArrowLineLeft />
          </div>
          <div css={targetWordAndArrowContainerCss}>
            <div css={targetWordContainerCss}>
              <label css={wordHeaderStylesCss} htmlFor={`word-${uniqueId}`}>
                Target Word
              </label>
              <AdjustFontSizeToFitContent>
                <input
                  id={`word-${uniqueId}`}
                  css={wordInputStylesCss}
                  type="text"
                  value={wordData?.word || ""}
                  onChange={(e) => onChange?.({ word: e.target.value })}
                  placeholder="Enter word"
                  data-qa-selector="word-0"
                  maxLength={WORD_MAX_LENGTH}
                  disabled={isStudent}
                />
              </AdjustFontSizeToFitContent>
            </div>
            <ArrowDown />
          </div>
          <div css={rightArmContainerCss}>
            <ArrowLineLeft css={arrowLineRightCss} />
          </div>
        </div>
        {/* Grouped Prefix, Root, Suffix with their meanings -- these fields are editable by students */}
        <div css={wordComponentsGridCss}>
          {WORD_COMPONENT_FIELDS.map((field) => {
            const meaningField = field.meaning;
            return (
              <div key={field.key} css={wordAndArrowContainerCss}>
                <div css={wordContainerCss}>
                  <div css={wordComponentColumnCss}>
                    <label
                      css={wordComponentLabelCss}
                      htmlFor={field.id(uniqueId)}
                    >
                      {field.label}:
                    </label>
                    <input
                      id={field.id(uniqueId)}
                      css={wordComponentInputCss}
                      type="text"
                      value={wordData[field.key] || ""}
                      onChange={(e) =>
                        onChange?.({ [field.key]: e.target.value })
                      }
                      placeholder={field?.placeholder ?? ""}
                      data-qa-selector={field.qa}
                      maxLength={field.maxLength}
                    />
                  </div>
                  <TemplateTextArea
                    value={wordData[meaningField.key] || ""}
                    maxLength={meaningField.maxLength}
                    css={wordComponentColumnCss}
                  >
                    <label
                      css={wordComponentLabelCss}
                      htmlFor={meaningField.id(uniqueId)}
                    >
                      Meaning:
                    </label>
                    <TemplateTextArea.TextArea
                      id={meaningField.id(uniqueId)}
                      onChange={(e) =>
                        onChange?.({ [meaningField.key]: e.target.value })
                      }
                      placeholder={meaningField.placeholder}
                    />
                    <TemplateTextArea.CharacterCount />
                  </TemplateTextArea>
                </div>
                <ArrowDown />
              </div>
            );
          })}
        </div>
        {/* Definition, Related Words, and Sentence -- these fields are editable by students */}
        {WORD_DEFINITION_AND_EXAMPLES_FIELDS.map((field, i) => (
          <div key={field.key} css={wordAndArrowContainerCss}>
            <div css={exampleTextAreaContainerCss}>
              <TemplateTextArea
                value={wordData[field.key] || ""}
                maxLength={field.maxLength}
              >
                <div css={wordComponentRowCss}>
                  <label css={exampleLabelCss} htmlFor={field.id(uniqueId)}>
                    {field.label}:
                  </label>
                  <TemplateTextArea.TextArea
                    id={field.id(uniqueId)}
                    css={exampleTextareaCss}
                    onChange={(e) =>
                      onChange?.({ [field.key]: e.target.value })
                    }
                    placeholder={field.placeholder}
                    data-qa-selector={field.qa}
                  />
                </div>
                <TemplateTextArea.CharacterCount />
              </TemplateTextArea>
            </div>
            {i < WORD_DEFINITION_AND_EXAMPLES_FIELDS.length - 1 && (
              <ArrowDown />
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default WordStudyOrganizer;
